import { useState, useEffect, useRef } from 'react';
import CryptoPatternChart from './CryptoPatternChart';

interface CryptoMatch {
  date: string;
  similarityScore: number;
  window: number[];
  outcome: number;
}
interface CryptoPatternData {
  coin: string; symbol: string; currentPrice: number;
  currentWindow: number[]; currentDates: string[];
  matches: CryptoMatch[]; bullishCount: number;
  avgOutcome: number; lookbackDays: number; forwardDays: number;
}

interface ScanMarket {
  marketId: string;
  question: string;
  yesPrice: number;
  noPrice: number;
  volume: number;
  resolveTime?: string;
  cryptoPattern?: CryptoPatternData;
  // first-pass result (added when scan_first_pass fires)
  firstPass?: boolean;
  firstPassReason?: string;
  // full eval result (added when market_evaluated fires)
  side?: 'YES' | 'NO' | 'SKIP';
  conviction?: number;
  trueProbability?: number;
  reasoning?: string;
}

interface ScanRun {
  id: string;
  timestamp: string;
  lookaheadLabel: string;
  fetched: number;
  markets: ScanMarket[];
  complete: boolean;
}

function fmtVol(v: number) {
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${v.toFixed(0)}`;
}
function fmtExpiry(ts?: string) {
  if (!ts) return '';
  try {
    return new Date(ts).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}
function fmtTime(ts: string) {
  try {
    return new Date(ts).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch { return ''; }
}

const ScanLog: React.FC = () => {
  const [runs, setRuns] = useState<ScanRun[]>(() => {
    try {
      const saved = localStorage.getItem('poly_scan_runs_v2');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const currentRunRef = useRef<string | null>(null);

  useEffect(() => {
    if (runs.length > 0) {
      try { localStorage.setItem('poly_scan_runs_v2', JSON.stringify(runs.slice(0, 10))); } catch {}
    }
  }, [runs]);

  useEffect(() => {
    const es = new EventSource('/api/stream');

    es.addEventListener('scan_started', (e: MessageEvent) => {
      try {
        const scan = JSON.parse(e.data);
        const id = scan.timestamp;
        currentRunRef.current = id;
        const lookaheadLabel = scan.lookaheadHours > 0 ? `next ${scan.lookaheadHours}h` : 'all markets';
        setRuns(prev => [{
          id, timestamp: scan.timestamp, lookaheadLabel,
          fetched: 0, markets: [], complete: false,
        }, ...prev].slice(0, 10));
        setExpandedId(id);
      } catch {}
    });

    // Markets are ready (pre-filtered) — render all cards immediately
    es.addEventListener('scan_markets_ready', (e: MessageEvent) => {
      try {
        const markets: ScanMarket[] = JSON.parse(e.data);
        const id = currentRunRef.current;
        if (!id) return;
        setRuns(prev => prev.map(r =>
          r.id === id ? { ...r, fetched: markets.length, markets } : r
        ));
      } catch {}
    });

    // First-pass result for a single market — update its card inline
    es.addEventListener('scan_first_pass', (e: MessageEvent) => {
      try {
        const { marketId, pass, reason } = JSON.parse(e.data);
        const id = currentRunRef.current;
        if (!id) return;
        setRuns(prev => prev.map(r => {
          if (r.id !== id) return r;
          return {
            ...r,
            markets: r.markets.map(m =>
              m.marketId === marketId ? { ...m, firstPass: pass, firstPassReason: reason } : m
            ),
          };
        }));
      } catch {}
    });

    // Full eval result — update the card with conviction/side/reasoning
    es.addEventListener('market_evaluated', (e: MessageEvent) => {
      try {
        const ev = JSON.parse(e.data);
        const id = currentRunRef.current;
        if (!id) return;
        setRuns(prev => prev.map(r => {
          if (r.id !== id) return r;
          const exists = r.markets.some(m => m.marketId === ev.marketId);
          const updatedMarkets = exists
            ? r.markets.map(m => m.marketId === ev.marketId
                ? { ...m, side: ev.side, conviction: ev.conviction, trueProbability: ev.trueProbability, reasoning: ev.reasoning }
                : m)
            : r.markets; // already in list from scan_markets_ready
          return { ...r, markets: updatedMarkets };
        }));
      } catch {}
    });

    es.addEventListener('status_updated', (e: MessageEvent) => {
      try {
        const s = JSON.parse(e.data);
        if (!s.isScanning && currentRunRef.current) {
          const id = currentRunRef.current;
          setRuns(prev => prev.map(r => r.id === id ? { ...r, complete: true } : r));
          currentRunRef.current = null;
        }
      } catch {}
    });

    return () => es.close();
  }, []);

  return (
    <div className="scan-log-panel">
      {runs.length === 0 && (
        <div className="scan-log-empty">No scans yet. Trigger a manual scan — results will populate here in real time.</div>
      )}
      {runs.map(run => (
        <div key={run.id} className="scan-run">
          <div className="scan-run-header" onClick={() => setExpandedId(prev => prev === run.id ? null : run.id)}>
            <span className="scan-run-toggle">{expandedId === run.id ? '▼' : '►'}</span>
            <span className="scan-run-time">{fmtTime(run.timestamp)}</span>
            <span className="scan-run-label">{run.lookaheadLabel}</span>
            <span className="scan-run-stats">
              {run.fetched > 0
                ? `${run.fetched} markets`
                : 'fetching…'}
            </span>
            {!run.complete && <span className="scan-run-live">●</span>}
          </div>

          {expandedId === run.id && (
            <div className="scan-run-markets">
              {run.markets.length === 0 && (
                <div className="scan-no-markets">Fetching markets…</div>
              )}
              {run.markets.filter(m => m.firstPass !== false).map((m) => {
                const sideClass = m.side ? `scan-market-${m.side.toLowerCase()}` : '';
                const firstPassPending = m.firstPass === undefined;
                return (
                  <div key={m.marketId} className={`scan-market-card ${sideClass}`}>
                    <div className="scan-market-top">
                      {/* First-pass badge */}
                      {firstPassPending && (
                        <span className="first-pass-screening">⚡ screening…</span>
                      )}
                      {!firstPassPending && m.firstPass && !m.side && m.firstPassReason && (
                        <span className="eval-pass-badge pass">✓ pass 1</span>
                      )}
                      {!firstPassPending && !m.firstPass && (
                        <span className="eval-pass-badge fail">✗ fail 1</span>
                      )}
                      {/* Full eval badge */}
                      {m.side && (
                        <span className={`scan-side-badge side-${m.side.toLowerCase()}`}>
                          {m.side}{m.conviction != null ? ` · ${m.conviction}` : ''}
                        </span>
                      )}
                      <span className="scan-market-question">{m.question}</span>
                    </div>
                    <div className="scan-market-meta">
                      <span className="scan-price yes">YES {Math.round(m.yesPrice * 100)}¢</span>
                      <span className="scan-price no">NO {Math.round(m.noPrice * 100)}¢</span>
                      <span className="scan-vol">{fmtVol(m.volume)}</span>
                      {m.resolveTime && <span className="scan-expiry">⏰ {fmtExpiry(m.resolveTime)}</span>}
                    </div>
                    {/* First-pass reason */}
                    {!firstPassPending && m.firstPass && !m.side && m.firstPassReason && (
                      <div className="scan-market-reasoning">{m.firstPassReason}</div>
                    )}
                    {/* Full eval reasoning */}
                    {m.reasoning && (
                      <div className="scan-market-reasoning">{m.reasoning}</div>
                    )}
                    {m.cryptoPattern && (
                      <CryptoPatternChart data={m.cryptoPattern} />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default ScanLog;
